import { Tag } from "antd";
import trans, {
	formatDuration,
	formatTime,
	splitDateTime,
} from "../../../utils";
import AntdTable from "../../antd/Table/Table";
import Actions from "../../antd/Table/Actions";
import { useContext, useEffect, useState } from "react";
import { popupcontext } from "../../../context/Popupscontext";
import { filtrationcontext } from "../../../context/Filtercontext";
import { useGetAllTasksQuery } from "../../../Redux/service/Tasks/Tasks";
import { useSelector } from "react-redux";
import usePermission from "../../../hooks/usePermission";

const TasksList = ({
	fromDate,
	toDate,
	setRefresh,
	refresh,
	setRefreshing,
}) => {
	const { edited, setEdited, setDeleteItem, deleteItem } =
		useContext(popupcontext);
	const {
		limit,
		setLimit,
		setSort: SetSortFilter,
		searchQuery,
		setSearchQuery,
	} = useContext(filtrationcontext);
	const [currentPage, setCurrentPage] = useState(1);
	const [sort, setSort] = useState({
		by: "",
		order: "",
	});
	const [tasks, setTasks] = useState([]);
	const Livetasks = useSelector((state) => state.webSocket.tasks);
	const {
		data: tasksData,
		isFetching,
		refetch,
	} = useGetAllTasksQuery(
		{
			page: currentPage,
			limit,
			from: fromDate,
			to: toDate,
			search: searchQuery,
			sortBy: sort.by,
			sortOrder: sort.order,
		},
		{ refetchOnMountOrArgChange: true }
	);

	const { edit, delete: canDelete } = usePermission("FIELD_TASKS");

	const sortFieldMap = {
		assignedTo: "assignedToName",
		createdBy: "createdByName",
		poi: "poiName",
	};

	useEffect(() => {
		setRefreshing?.(isFetching);
	}, [isFetching, setRefreshing]);

	useEffect(() => {
		if (tasksData?.data) {
			setTasks(tasksData?.data || []);
		}
	}, [tasksData]);

	const statusColors = {
		todo: `blue`,
		inProgress: `orange`,
		done: `green`,
		// delayed: `red`,
		unassigned: `default`,
	};

	useEffect(() => {
		async function fetchDataAfterDelete() {
			if (deleteItem.condition === true && deleteItem.value === "task") {
				const res = await refetch();
				const currentData = res?.data?.data || [];

				if (currentData.length === 0 && currentPage > 1) {
					setCurrentPage((prev) => prev - 1);
				}

				setDeleteItem({ condition: null, value: null });
			}
		}
		fetchDataAfterDelete();
	}, [
		deleteItem.condition,
		deleteItem.value,
		currentPage,
		refetch,
		setDeleteItem,
	]);

	useEffect(() => {
		async function fetchdata() {
			if (edited) {
				await refetch();
				setEdited(false);
			}
			if (refresh) {
				await refetch();
				setRefresh(false);
			}
			if (Livetasks) {
				await refetch();
			}
		}
		fetchdata();
	}, [edited, refresh, Livetasks]);

	const columns = [
		{
			title: trans("Task Name"),
			dataIndex: "name",
			sorter: true,
			fixed: "left",
			key: "name",
			width: 200,
			render: (name) => <span>{name}</span>,
		},
		{
			title: trans("Start Time"),
			dataIndex: "startTime",
			key: "startTime",
			width: 120,
			render: (value) => splitDateTime(value).time,
		},

		{
			title: trans("Duration"),
			dataIndex: "expectedDuration",
			key: "expectedDuration",
			sorter: true,
			width: 120,
			render: (value) => <span>{formatDuration(value)}</span>,
		},
		{
			title: trans("Priority"),
			dataIndex: "priority",
			key: "priority",
			width: 120,
			render: (value) => (
				<span className="text-capitalize">{trans(value)}</span>
			),
		},
		{
			title: trans("POI Name"),
			dataIndex: "poi",
			key: "poi",
			sorter: true,
			width: 200,
			render: (poi) => <span>{poi?.name || "Not assigned"}</span>,
		},
		{
			title: trans("status"),
			dataIndex: "status",
			key: "status",
			sorter: true,
			width: 120,
			render: (value) => (
				<Tag
					color={statusColors[value] || statusColors.unassigned}
					className="text-capitalize"
				>
					{trans(value)}
				</Tag>
			),
		},
		{
			title: trans("Assigned To"),
			dataIndex: "assignedTo",
			sorter: true,
			key: "assignedTo",
			width: 200,
			render: (value) => (
				<span className="text-capitalize">
					{value ? value.name || "Unassigned" : "Unassigned"}
				</span>
			),
		},
		{
			title: trans("Type"),
			dataIndex: "type",
			sorter: true,
			key: "type",
			width: 120,
			render: (value) => (
				<span className="text-capitalize">{trans(value) || "No data"}</span>
			),
		},
		{
			title: trans("Start Date"),
			dataIndex: "startTime",
			sorter: true,
			key: "startTime",
			width: 120,
			render: (value) => splitDateTime(value).date,
		},
		{
			title: trans("Created By"),
			dataIndex: "createdBy",
			sorter: true,
			key: "createdBy",
			width: 120,
			render: (value) => (
				<span className="text-capitalize">
					{trans(value.name) || "No data"}
				</span>
			),
		},
		{
			title: trans("last update"),
			dataIndex: "updatedAt",
			key: "updatedAt",
			width: 120,
			render: (value) => splitDateTime(value).date,
		},
		{
			title: trans("Actions"),
			key: "actions",
			render: (_, record) => (
				<Actions
					taskId={record.id}
					name={record.name}
					canEdit={edit}
					canDelete={canDelete}
				/>
			),
		},
	];

	const handleTableChange = (pagination, filters, sorter) => {
		if (sorter.order) {
			const apiSortField = sortFieldMap[sorter.field] || sorter.field;
			setSort({
				by: apiSortField,
				order: sorter.order === "ascend" ? "asc" : "desc",
			});
		} else {
			setSort({
				by: "",
				order: "",
			});
		}
	};

	return (
		<div className="bg-white-rounded-top mt-3 table-parent">
			<AntdTable
				columns={columns}
				dataSource={tasks || []}
				loading={isFetching}
				pageSize={limit}
				totalItems={tasksData?.totalResults ?? 0}
				currentPage={currentPage}
				onPageChange={(page) => setCurrentPage(page)}
				onLimitChange={(newLimit) => {
					setLimit(newLimit);
					setCurrentPage(1);
				}}
				onTableChange={handleTableChange}
			/>
		</div>
	);
};

export default TasksList;
